import React, { useState, useEffect } from 'react';
import './ExportModal.css';

function ExportModal({ isOpen, onClose, questions, onExport, isExporting }) {
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());

  useEffect(() => {
    // Reset selections when modal opens
    if (isOpen) {
      setSelectedQuestions([]);
      setSelectAll(false);
      setExpandedQuestions(new Set());
    }
  }, [isOpen]);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedQuestions([]);
      setSelectAll(false);
    } else {
      setSelectedQuestions(questions.map(q => q.id));
      setSelectAll(true);
    }
  };

  const handleToggleQuestion = (questionId) => {
    setSelectedQuestions(prev => {
      if (prev.includes(questionId)) {
        const newSelected = prev.filter(id => id !== questionId);
        setSelectAll(newSelected.length === questions.length);
        return newSelected;
      } else {
        const newSelected = [...prev, questionId];
        setSelectAll(newSelected.length === questions.length);
        return newSelected;
      }
    });
  };

  const handleExport = () => {
    if (selectedQuestions.length === 0) {
      alert('Please select at least one question to export');
      return;
    }
    // If all questions are selected, send null to export all
    if (selectedQuestions.length === questions.length) {
      onExport(null);
    } else {
      onExport(selectedQuestions);
    }
  };

  const toggleExpand = (questionId) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="export-modal-overlay" onClick={onClose}>
      <div className="export-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="export-modal-header">
          <h2>Select Questions to Export</h2>
          <button className="export-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="export-modal-body">
          <div className="select-all-container">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="checkbox-input"
              />
              <span className="checkbox-text">Select All ({questions.length} questions)</span>
            </label>
          </div>

          <div className="questions-list">
            {questions.map((question, index) => {
              const isExpanded = expandedQuestions.has(question.id);
              return (
                <div key={question.id} className="question-item">
                  <div className="question-item-header">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedQuestions.includes(question.id)}
                        onChange={() => handleToggleQuestion(question.id)}
                        className="checkbox-input"
                      />
                      <div className="question-preview">
                        <span className="question-number">Q{index + 1}:</span>
                        <span className="question-text-preview">
                          {isExpanded 
                            ? question.text 
                            : (question.text.length > 100 
                                ? question.text.substring(0, 100) + '...' 
                                : question.text)
                          }
                        </span>
                      </div>
                    </label>
                    <button 
                      className="expand-button"
                      onClick={() => toggleExpand(question.id)}
                      title={isExpanded ? "Collapse" : "Expand"}
                    >
                      {isExpanded ? '▲' : '▼'}
                    </button>
                  </div>
                  
                  {isExpanded && (
                    <div className="question-details">
                      {question.type === 'multiple_choice' && question.options && question.options.length > 0 && (
                        <div className="question-options">
                          <strong>Options:</strong>
                          <ul>
                            {question.options.map((option, optIdx) => (
                              <li key={option.id || optIdx} className={option.is_correct ? 'correct-option' : ''}>
                                {String.fromCharCode(65 + optIdx)}. {option.text}
                                {option.is_correct && <span className="correct-badge"> ✓ Correct</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {question.difficulty && (
                        <div className="question-meta">
                          <span><strong>Difficulty:</strong> {question.difficulty}</span>
                        </div>
                      )}
                      {question.blooms_taxonomy && (
                        <div className="question-meta">
                          <span><strong>Bloom's Taxonomy:</strong> {question.blooms_taxonomy}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="export-modal-footer">
            <div className="selected-count">
              {selectedQuestions.length} of {questions.length} questions selected
            </div>
            <div className="export-modal-buttons">
              <button 
                className="btn btn-secondary" 
                onClick={onClose}
                disabled={isExporting}
              >
                Cancel
              </button>
              <button 
                className="btn btn-success" 
                onClick={handleExport}
                disabled={isExporting || selectedQuestions.length === 0}
              >
                {isExporting ? '⏳ Exporting...' : `📤 Export ${selectedQuestions.length} Question${selectedQuestions.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExportModal;
